import {
  COLOURS, COLS, ROWS, decodePage, g0ToUnicode, linkAt, sixels,
  type Cell, type Enhancement, type Link, type Row,
} from '@textbox/core';

export interface RenderOptions {
  reveal: boolean;
  mix: boolean;
  links: Link[];
  hover: Link | null;
  /** Colour index for the eight address cells of the header (7 white, 2 green while searching). */
  addressColour: number;
  enhancements?: Enhancement[];
}

/**
 * DOM grid renderer: 25 × 40 absolutely positioned cells created once and
 * patched in place. Each cell is keyed by a signature so an unchanged
 * cell costs one string compare; the clock only touches row 0.
 */
export class ScreenRenderer {
  private cells: HTMLElement[][] = [];
  private sigs: string[][] = [];
  private linkGrid: (Link | null)[][] = [];
  private hoverKey = '';

  readonly root: HTMLElement;

  constructor(root: HTMLElement) {
    this.root = root;
    root.replaceChildren();
    for (let r = 0; r < ROWS; r++) {
      const rowEl = document.createElement('div');
      rowEl.className = 'row';
      const cellsRow: HTMLElement[] = [];
      const sigRow: string[] = [];
      for (let x = 0; x < COLS; x++) {
        const el = document.createElement('div');
        el.className = 'c';
        el.dataset.r = String(r);
        el.dataset.x = String(x);
        rowEl.appendChild(el);
        cellsRow.push(el);
        sigRow.push('');
      }
      root.appendChild(rowEl);
      this.cells.push(cellsRow);
      this.sigs.push(sigRow);
      this.linkGrid.push(new Array(COLS).fill(null));
    }
  }

  render(rows: Row[], opts: RenderOptions): void {
    const decoded = decodePage(rows);
    const enh = new Map<string, string>();
    for (const e of opts.enhancements ?? []) enh.set(`${e.row}:${e.col}`, e.char);

    for (let r = 0; r < ROWS; r++) {
      const dr = decoded[r]!;
      for (let x = 0; x < COLS; x++) {
        const cell = dr.cells[x]!;
        const fg = r === 0 && x < 8 ? opts.addressColour : cell.fg;
        const link = r === 0 ? null : (linkAt(opts.links, r, x) ?? null);
        this.linkGrid[r]![x] = link;
        const hot = !!link && !!opts.hover && link === opts.hover;
        const hidden = cell.conceal && !opts.reveal;
        const override = enh.get(`${r}:${x}`);
        const sig = `${cell.glyph}|${fg}|${cell.bg}|${cell.mosaic ? 1 : 0}${cell.sep ? 1 : 0}${cell.flash ? 1 : 0}${cell.dh ? 1 : 0}${cell.lower ? 1 : 0}${hidden ? 1 : 0}${hot ? 1 : 0}${opts.mix ? 1 : 0}|${link ? linkKey(link) : ''}|${override ?? ''}`;
        if (sig === this.sigs[r]![x]) continue;
        this.sigs[r]![x] = sig;
        this.paint(this.cells[r]![x]!, cell, fg, hidden, hot, opts.mix, link, override);
      }
    }
  }

  private paint(el: HTMLElement, cell: Cell, fg: number, hidden: boolean, hot: boolean, mix: boolean, link: Link | null, override: string | undefined): void {
    let cls = 'c';
    if (cell.mosaic) cls += cell.sep ? ' m sep' : ' m';
    if (cell.flash) cls += ' flash';
    if (cell.dh) cls += cell.lower ? ' dh lower' : ' dh';
    if (hidden) cls += ' hid';
    if (link) cls += ' link';
    if (hot) cls += ' hot';
    el.className = cls;
    el.style.color = COLOURS[fg]!;
    el.style.background = cell.bg === 0 ? (mix ? 'transparent' : COLOURS[0]!) : COLOURS[cell.bg]!;
    if (link) {
      el.title = describeLink(link);
    } else if (el.title) {
      el.removeAttribute('title');
    }
    if (cell.mosaic) {
      const bits = sixels(cell.glyph);
      let inner = '';
      for (let i = 0; i < 6; i++) inner += bits[i] ? '<i class="on"></i>' : '<i></i>';
      el.innerHTML = `<span class="g">${inner}</span>`;
    } else {
      const ch = override ?? g0ToUnicode(cell.glyph);
      el.innerHTML = '';
      const span = document.createElement('span');
      span.className = 'g';
      span.textContent = ch;
      el.appendChild(span);
    }
  }

  /** Link under a pointer event target, or null. */
  hitTest(target: EventTarget | null): { link: Link | null; row: number; col: number } | null {
    const el = (target as HTMLElement | null)?.closest?.('.c') as HTMLElement | null;
    if (!el || !this.root.contains(el)) return null;
    const row = Number(el.dataset.r);
    const col = Number(el.dataset.x);
    return { link: this.linkGrid[row]?.[col] ?? null, row, col };
  }

  setHover(link: Link | null): boolean {
    const key = link ? linkKey(link) : '';
    if (key === this.hoverKey) return false;
    this.hoverKey = key;
    return true;
  }
}

export function linkKey(l: Link): string {
  return `${l.row}:${l.colStart}:${l.colEnd}`;
}

export function describeLink(l: Link): string {
  if ('url' in l.target) return l.target.url;
  return `Page ${l.target.page}${l.target.sub ? ` subpage ${l.target.sub}` : ''}`;
}
